import React, { useState, useEffect, useCallback } from "react";
import Login from "./Login";
import GestionaleConsulenteDelLavoro from "./App";
import DesignPreviewPage from "./pages/DesignPreviewPage";
import { Toaster } from "@/components/ui/sonner";
import api from "./api";
import useInactivityLock from "./hooks/useInactivityLock";

// Fallback usato finché non sono state caricate le preferenze condivise.
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
const LAST_EMAIL_STORAGE_KEY = "last_login_email";

// Design Preview — accessibile via `#design-preview` in URL.
// Mockup statici (no DB, no auth) per validare la direzione grafica v2
// prima del rollout. Riferimento: Brand Book v1.0 · 27/07/2026.
function useIsDesignPreview() {
  const [isPreview, setIsPreview] = React.useState(() =>
    typeof window !== "undefined" && window.location.hash === "#design-preview"
  );
  React.useEffect(() => {
    const onHash = () => setIsPreview(window.location.hash === "#design-preview");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return isPreview;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
            <div className="text-4xl mb-4">⚠</div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Si è verificato un errore</h2>
            <p className="text-sm text-slate-500 mb-4">
              {this.state.error?.message || "Errore imprevisto nell'applicazione"}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              Ricarica pagina
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AppWrapper() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // When the session is force-locked (currently: inactivity timeout) we
  // surface a reason so the Login screen can show the matching banner.
  const [lockReason, setLockReason] = useState(null);
  const [inactivityTimeoutMinutes, setInactivityTimeoutMinutes] = useState(10);
  const isDesignPreview = useIsDesignPreview();

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    api.getSettings()
      .then((settings) => {
        const minutes = Number(settings?.inactivityTimeoutMinutes);
        if (!cancelled && Number.isFinite(minutes) && minutes >= 5 && minutes <= 120) {
          setInactivityTimeoutMinutes(minutes);
        }
      })
      .catch(() => { /* mantiene il fallback sicuro */ });

    const onTimeoutChanged = (event) => {
      const minutes = Number(event?.detail?.minutes);
      if (Number.isFinite(minutes) && minutes >= 5 && minutes <= 120) {
        setInactivityTimeoutMinutes(minutes);
      }
    };
    window.addEventListener("gi:inactivity-timeout-changed", onTimeoutChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("gi:inactivity-timeout-changed", onTimeoutChanged);
    };
  }, [user]);

  // Security/Privacy (Feb 2026) — bfcache guard.
  // Quando il browser ripristina la pagina dal Back/Forward cache
  // (`event.persisted === true`) lo stato React può essere intatto pur
  // senza un token valido. Forziamo una ri-validazione completa così
  // che, dopo logout, premere "Indietro" porti SEMPRE alla schermata
  // di login.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event && event.persisted) {
        // Hard reload — il browser scarta la cache e rifa il bootstrap.
        try { window.location.reload(); } catch (_) { /* noop */ }
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const checkAuth = async () => {
    try {
      const currentUser = await api.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.log("Non autenticato");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (userData, email) => {
    // Remember only the email/username so the Login screen can pre-fill
    // it next time. The password is NEVER persisted (no localStorage,
    // sessionStorage or cookies) — see Login.js for the input config.
    if (email) {
      try { localStorage.setItem(LAST_EMAIL_STORAGE_KEY, email); } catch { /* ignore */ }
    }
    setLockReason(null);
    setUser(userData);
  };

  const handleLogout = useCallback(() => {
    api.logout();
    setUser(null);
    // Manual logout is NOT an inactivity lock → clear any pending banner.
    setLockReason(null);
    // Security/Privacy (Feb 2026) — Dopo logout: forza un page replace
    // per (a) cancellare TUTTO lo stato React/memoria in-flight delle
    // pagine autenticate, (b) sostituire l'entry corrente nella history
    // del browser così che il tasto Back NON possa riportare l'utente
    // su una vista protetta cacheata. `replace` (vs `assign`) evita di
    // poter "tornare indietro" su una rotta autenticata.
    try {
      window.location.replace("/");
    } catch (_) { /* noop */ }
  }, []);

  // Inactivity lock handler — clears the auth token + React user state and
  // routes back to the login screen with a "Sessione scaduta" banner. We
  // keep `last_login_email` so the operator can simply re-type the
  // password.
  const handleInactivityTimeout = useCallback(() => {
    api.logout();
    setUser((prev) => (prev ? null : prev));
    setLockReason("inactivity");
  }, []);

  useInactivityLock({
    active: !!user,
    timeoutMs:
      // Test/debug hook: lets the regression script verify the lock with
      // a short timeout (set BEFORE login). In produzione usa la
      // preferenza condivisa dello Studio.
      (typeof window !== "undefined" && Number(window.__INACTIVITY_TIMEOUT_MS__)) ||
      (inactivityTimeoutMinutes * 60 * 1000) ||
      INACTIVITY_TIMEOUT_MS,
    onTimeout: handleInactivityTimeout,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Design Preview route (#design-preview) — mockup statici per validazione
  // direzione grafica. Non richiede login, non tocca DB/API.
  if (isDesignPreview) {
    return (
      <ErrorBoundary>
        <DesignPreviewPage />
      </ErrorBoundary>
    );
  }

  if (!user) {
    return (
      <ErrorBoundary>
        <Login onLoginSuccess={handleLoginSuccess} lockReason={lockReason} />
        <Toaster
          richColors
          closeButton
          position="bottom-right"
          duration={1800}
          visibleToasts={3}
          gap={6}
        />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <GestionaleConsulenteDelLavoro user={user} onLogout={handleLogout} />
      {/* 27/02/2026 — Toast globale spostato in basso a destra e durata
          ridotta a 1.8s: in top-right interferiva con tab/header e
          rimaneva 4s (default Sonner). Gli operatori segnalavano blocco
          percepito quando cambiavano area subito dopo un save. */}
      <Toaster
        richColors
        closeButton
        position="bottom-right"
        duration={1800}
        visibleToasts={3}
        gap={6}
      />
    </ErrorBoundary>
  );
}
